import React from "react";
import styled from "styled-components";
import { VirtualSpace } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import { getSpaceThumbnail } from "@lifeat/shared/spaces/SpacesUtil";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { useVirtualSpaceContext } from "@contexts/VirtualSpaceContext";
import ImagePlaceholderIcon from "@icons/ImagePlaceholder.svg";
import SavedSpaceIcon from "@icons/SpaceSavedIcon.svg";
import { FontSize } from "@themes/Fonts";
import { SignUpCTASource } from "@user/AmplitudeUserEvents";

const Container = styled.div`
    display: flex;
    width: 155px;
`;

const SpaceThumbnailWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
`;

const Thumbnail = styled.div`
    display: flex;
    justify-content: center;
    align-items: stretch;
    height: 80px;
    width: 100%;
    border-radius: 7px;
    position: relative;
    background: ${({ theme }) => theme.backgroundSpecial};
    cursor: pointer;

    &:hover {
        scale: 1.025;
    }

    transition: scale 0.2s ease-in-out;
`;

const ThumbnailImg = styled.img`
    height: inherit;
    width: inherit;
    border-radius: inherit;
    object-fit: cover;
`;

const Placeholder = styled(ImagePlaceholderIcon)`
    fill: ${({ theme }) => theme.iconColor};
    margin: 16px;
`;

const SpaceName = styled.span`
    font-size: ${FontSize.small};
    width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;
const StyledSavedSpaceIcon = styled(SavedSpaceIcon)<{ $fill: boolean }>`
    fill: ${(props) => (props.$fill ? "white" : "transparent")};
    stroke: white;
    scale: 0.6;

    &:hover {
        fill: white;
        opacity: 0.7;
    }
`;

const SaveSpaceButton = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
`;

interface SpaceThumbnailProps {
    space: VirtualSpace;
    handleSelectSpace: () => void;
}

export default function SpaceThumbnail(props: SpaceThumbnailProps) {
    const { user, userData }: UserContextType = useUserContext();
    const { spaceController } = useVirtualSpaceContext();
    const { setSignInView }: CallToActionContextType = useCallToActionContext();

    const isSpaceSaved = spaceController.isSpaceSaved(props.space);
    const thumbnailUrl = getSpaceThumbnail(props.space.videoUrl);

    return (
        <Container>
            {!thumbnailUrl && (
                <Thumbnail>
                    <Placeholder />
                </Thumbnail>
            )}
            {thumbnailUrl && (
                <SpaceThumbnailWrapper onClick={props.handleSelectSpace}>
                    <Thumbnail>
                        <ThumbnailImg
                            onClick={() => {
                                props.handleSelectSpace();
                            }}
                            src={thumbnailUrl}
                            alt="space thumbnail"
                        />
                        <SaveSpaceButton
                            onClick={(e: React.MouseEvent) => {
                                if (user && userData) {
                                    e.stopPropagation();
                                    spaceController.toggleSpaceSaved(!isSpaceSaved, props.space);
                                } else {
                                    setSignInView(SignUpCTASource.SAVE_SPACE);
                                }
                            }}
                        >
                            <StyledSavedSpaceIcon $fill={isSpaceSaved} />
                        </SaveSpaceButton>
                    </Thumbnail>

                    <SpaceName>{props.space.name}</SpaceName>
                </SpaceThumbnailWrapper>
            )}
        </Container>
    );
}
